import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { FileText, Clock, ChevronRight, Plus, Camera, Loader2 } from "lucide-react";

const STATUS_COLOR = {
  draft: "bg-amber-50 text-amber-600",
  active: "bg-green-50 text-green-600",
};

export default function MyCases() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/cases"); setCases(data); }
    catch { toast.error("Could not load cases"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startAccident = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/cases", { practice_area: "auto_accident" });
      navigate(`/new-accident?case=${data.case_id}`);
    } catch { toast.error("Could not start a new report"); setCreating(false); }
  };

  return (
    <AppLayout>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-widest">Your Reports</p>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-navy mt-1">My Cases</h1>
          <p className="mt-2 text-slate-600">Open a case to review, edit, and add information.</p>
        </div>
        <button onClick={startAccident} disabled={creating} data-testid="new-case-btn" className="hidden sm:flex items-center gap-2 px-5 py-3 rounded-full bg-cobalt text-white font-medium hover:bg-cobalt-hover transition-all shadow-md active:scale-95">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} New Case
        </button>
      </div>

      <div className="mt-8 space-y-3">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>
        ) : cases.length === 0 ? (
          <div className="bg-white rounded-3xl border border-dashed border-slate-200 p-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto" />
            <p className="mt-3 text-slate-500">No cases yet. Start your first accident report.</p>
            <button onClick={startAccident} disabled={creating} className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cobalt text-white font-medium hover:bg-cobalt-hover transition-all active:scale-95">
              <Plus className="w-4 h-4" /> Start New Accident
            </button>
          </div>
        ) : cases.map((c) => (
          <Link key={c.case_id} to={`/cases/${c.case_id}`} data-testid={`mycase-${c.case_id}`}
            className="bg-white rounded-2xl border border-slate-100 p-5 flex items-center justify-between hover:-translate-y-0.5 hover:shadow-md transition-all">
            <div className="flex items-center gap-4">
              <div className="h-11 w-11 rounded-xl bg-cobalt-light flex items-center justify-center"><FileText className="w-5 h-5 text-cobalt" /></div>
              <div>
                <div className="font-semibold text-navy">{c.title}</div>
                <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                  <Clock className="w-3 h-3" /> {new Date(c.created_at).toLocaleDateString()}
                  <span className="flex items-center gap-1"><Camera className="w-3 h-3" /> {c.evidence_count} files</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS_COLOR[c.status] || "bg-slate-100 text-slate-500"}`}>{c.status}</span>
              <ChevronRight className="w-5 h-5 text-slate-300" />
            </div>
          </Link>
        ))}
      </div>

      <button onClick={startAccident} disabled={creating} className="sm:hidden fixed bottom-6 left-6 z-40 flex items-center gap-2 bg-cobalt text-white px-5 py-3.5 rounded-full shadow-xl font-semibold active:scale-95">
        <Plus className="w-5 h-5" /> New Case
      </button>
    </AppLayout>
  );
}
